import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { ISuperficie, ISelectOption } from "../types/superficie.types";
import { RetornoMidia, RetornoNomeMidia, RetornoRemocaoMidia } from "../../types/midia.types";

// ===== CRUD de Superfície =====

export const obterSuperficiePorId = async (idSuperficie: number): Promise<ISuperficie | null> => {
  try {
    const { data } = await api.get(`/SuperficieN1/ObterPorId/${idSuperficie}`);

    if (!data) return null;

    return {
      idSuperficie: data.idSuperficie,
      codigo: data.codigo || "",
      codigoInterno: data.codigoInterno,
      descricao: data.descricao || "",
      idSituacao: data.idSituacao || 1,
      idStatus: data.idStatus || 1,
      idReferencia: data.idReferencia,
      idLinha: data.idLinha,
      // FASE 05 — 4ª validação/Frente 9 2026-07-30: o Grupo é usado direto (sem tradução
      // para a antiga "categoria" fixa) — a tela decide os condicionais pela descrição do grupo.
      idGrupo: data.idGrupo,
      idCor: data.idCor,
      idUnidadeValorizacao: data.idUnidadeItemEmbalagem,
      precoVariavel: data.precoVariavel, // Se existir no backend
      idTipoTecido: data.idTipoTecido, // Se existir no backend
      idCorTecido: data.idCorTecido, // Se existir no backend
      acionamento: data.acionamento, // legado (texto)
      voltagem: data.voltagem, // FASE 05 — 6ª val. 2026-08-06: Voltagem numérica (TELA RETRÁTIL)
      // FASE 05 — 4ª validação/Frente 9 2026-07-30: dimensões da TELA MOSQUITEIRO (campos novos da API).
      larguraMinima: data.larguraMinima,
      larguraMaxima: data.larguraMaxima,
      alturaMinima: data.alturaMinima,
      alturaMaxima: data.alturaMaxima,
      peso: data.peso,
      passo: data.passo?.toString(),
      terminal: data.terminal, // Se existir no backend
      ipi: data.ipi,
      idImportacaoMaterial: data.idImportacaoMaterial,
      
      imagemUrl: data.imagem || undefined,
      imagemNomeArquivo: data.imagemNomeArquivo || undefined,
      
      dataUltimaAtualizacao: data.dataAlteracao ? new Date(data.dataAlteracao).toISOString() : undefined,
      dataCriacao: data.dataCriacao ? new Date(data.dataCriacao).toISOString() : undefined,
      editavel: data.editavel,
      excluido: data.excluido,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter superfície");
    return null;
  }
};

export const salvarSuperficie = async (superficie: Partial<ISuperficie>): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const isEdicao = !!superficie.idSuperficie;
    const endpoint = isEdicao ? `/SuperficieN1/Atualizar/${superficie.idSuperficie}` : '/SuperficieN1/Criar';
    const method = isEdicao ? 'put' : 'post';

    const dadosParaEnviar: any = { ...superficie };

    delete dadosParaEnviar.imagemUrl;
    delete dadosParaEnviar.imagemNomeArquivo;

    // FASE 05 — 4ª validação/Frente 9 2026-07-30: o Grupo já é o id do Cadastro de Grupos —
    // vai direto como idGrupoMaterial, sem tradução por "categoria" fixa.
    if (superficie.idGrupo) {
      dadosParaEnviar.idGrupoMaterial = superficie.idGrupo;
    }
    delete dadosParaEnviar.idGrupo;

    // Mapear campos do frontend para o backend
    dadosParaEnviar.idUnidadeItemEmbalagem = dadosParaEnviar.idUnidadeValorizacao;
    delete dadosParaEnviar.idUnidadeValorizacao;
    
    // Mapear IdCorMaterial
    dadosParaEnviar.idCorMaterial = dadosParaEnviar.idCor;
    delete dadosParaEnviar.idCor;

    const { data } = await api[method](endpoint, dadosParaEnviar);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar superfície");
    return null;
  }
};

export const excluirSuperficie = async (idSuperficie: number): Promise<boolean> => {
  try {
    const { data } = await api.delete(`/SuperficieN1/Excluir/${idSuperficie}`);
    return data.sucesso;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao excluir superfície");
    return false;
  }
};

export const duplicarSuperficie = async (idSuperficie: number): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const { data } = await api.post(`/SuperficieN1/Duplicar/${idSuperficie}`);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao duplicar superfície");
    return null;
  }
};

export const validarCodigoUnico = async (codigo: string, idExcluir?: number): Promise<boolean> => {
  try {
    const params: any = { codigo };
    if (idExcluir) params.idExcluir = idExcluir;
    
    const { data } = await api.get('/SuperficieN1/ValidarCodigoUnico', { params });
    return data.disponivel;
  } catch (error: any) {
    console.error("Erro ao validar código:", error);
    return false;
  }
};

// ===== Listas de Referência =====

export const listarReferencias = async (idTipoTela?: number): Promise<ISelectOption[]> => {
  try {
    const params: any = {};
    if (idTipoTela) params.idTipoTela = idTipoTela;
    const { data } = await api.get('/ReferenciaDados/Referencias', { params });
    return (data || []).map((item: any) => ({ id: item.idReferencia || item.id, nome: item.descricao || item.nome }));
  } catch (error: any) {
    console.error("Erro ao listar referências:", error);
    return [];
  }
};

export const listarLinhas = async (idReferencia?: number): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — S3/PO 2026-05-13: quando idReferencia informado, filtra via tabela ReferenciaLinhas.
    const params: any = {};
    if (idReferencia) params.idReferencia = idReferencia;
    const { data } = await api.get('/ReferenciaDados/Linhas', { params });
    return (data || []).map((item: any) => ({ id: item.idLinha || item.id, nome: item.descricao || item.nome }));
  } catch (error: any) {
    console.error("Erro ao listar linhas:", error);
    return [];
  }
};

export const listarGrupos = async (): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: só grupos com a flag Superfície do Cadastro de Grupos.
    const { data } = await api.get('/ReferenciaDados/Grupos', { params: { tipoMaterial: 'superficies' } });
    return (data || []).map((item: any) => ({ id: item.id || item.idGrupoMaterial, nome: item.nome || item.descricao }));
  } catch (error: any) {
    console.error("Erro ao listar grupos:", error);
    return [];
  }
};

export const listarCores = async (): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: só cores com a flag Superfície do cadastro de cores.
    const { data } = await api.get('/ReferenciaDados/Cores', { params: { tipoMaterial: 'superficies' } });
    return (data || []).map((item: any) => ({ id: item.id || item.idCorMaterial, nome: item.nome || item.descricao }));
  } catch (error: any) {
    console.error("Erro ao listar cores:", error);
    return [];
  }
};

export const listarUnidadesValorizacao = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get('/ReferenciaDados/UnidadesValor');
    return (data || []).map((item: any) => ({ id: item.id || item.idUnidadeItemEmbalagem, nome: item.nome || item.descricao }));
  } catch (error: any) {
    console.error("Erro ao listar unidades de valorização:", error);
    return [];
  }
};

export const listarTiposTecido = async (): Promise<ISelectOption[]> => {
  try {
    const response = await api.get('/TipoTecido/ativos');
    // BaseController retorna { statusCode, success, data }
    // MainController retorna diretamente o objeto
    // Verificar ambos os formatos
    let dataArray: any[] = [];
    
    if (response.data) {
      // Se response.data é um array, usar diretamente
      if (Array.isArray(response.data)) {
        dataArray = response.data;
      }
      // Se response.data tem propriedade 'data' (BaseController)
      else if (response.data.data && Array.isArray(response.data.data)) {
        dataArray = response.data.data;
      }
      // Se response.data tem propriedade 'items' (PagedResponse)
      else if (response.data.items && Array.isArray(response.data.items)) {
        dataArray = response.data.items;
      }
    }
    
    if (!dataArray || dataArray.length === 0) {
      console.warn("Resposta de tipos de tecido vazia ou inválida:", response.data);
      return [];
    }
    
    // O backend retorna em camelCase devido ao JsonNamingPolicy.CamelCase
    return dataArray.map((item: any) => ({ 
      id: item.idTipoTecido || item.id || 0, 
      nome: item.descricao || item.nome || '' 
    }));
  } catch (error: any) {
    console.error("Erro ao listar tipos de tecido:", error);
    // Não lançar erro, apenas retornar array vazio para não bloquear outros selects
    return [];
  }
};

// ===== Gerenciamento de Mídias =====

// Plano 21/09/2026 (Frente 3): as escritas de mídia devolvem também o `id` do registro gravado —
// com o produto Disponível, a API grava na cópia de trabalho e a tela precisa seguir para ela.
export const uploadImagem = async (idSuperficie: number, file: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', file);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);
    const { data } = await api.post(`/SuperficieN1/${idSuperficie}/Midias/UploadImagem`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data?.url ? { url: data.url, id: data.id ?? idSuperficie } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload da imagem");
    return null;
  }
};

export const removerImagem = async (idSuperficie: number): Promise<RetornoRemocaoMidia | null> => {
  try {
    const { data } = await api.delete(`/SuperficieN1/${idSuperficie}/Midias/RemoverImagem`);
    return data?.sucesso ? { sucesso: true, id: data.id ?? idSuperficie } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover imagem");
    return null;
  }
};

// ===== Histórico =====

export const obterHistoricoSuperficie = async (idSuperficie: number): Promise<any[] | null> => {
  try {
    const { data } = await api.get(`/SuperficieN1/${idSuperficie}/Historico`);
    return data?.items || [];
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter histórico de atualizações");
    return null;
  }
};

// ===== Definir apenas o NOME do arquivo (sem upload) =====

/**
 * Reporte de 31/08 (item 6): grava só o nome informado, sem enviar arquivo — é assim que o
 * produto assume uma mídia que já foi carregada por outro produto da empresa (F05A-P3b).
 * Devolve `arquivoExiste` para a tela avisar quando o nome ainda não tem arquivo no acervo.
 */
export const definirNomeMidiaSuperficie = async (
  idSuperficie: number,
  nomeArquivo: string
): Promise<RetornoNomeMidia | null> => {
  try {
    const { data } = await api.put(`/SuperficieN1/${idSuperficie}/Midias/DefinirNome`, {
      tipoMidia: "figura",
      nomeArquivo,
    });
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao gravar o nome do arquivo");
    return null;
  }
};
